import { useTranslation } from "react-i18next";
import { Alert } from "react-daisyui";
import { useStepperContext } from "@/components/shared/Stepper/StepperContext.tsx";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { tripCreateSchema } from "$/requests/TripCreateRequest.ts";
import StepIndicator from "@/components/ui/StepIndicator/StepIndicator.tsx";
import ChevronRightIcon from "@/components/ui/icons/ChevronRightIcon.tsx";
import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";
import type { TripData } from "$/models/Trip.model.ts";
import DateFlexibility, {
  allDateFlexibility,
} from "$/_const/trip/DateFlexibility.ts";
import type { OnCompleteArgs } from "@/components/functional/TripManipulationForm/TripManipulationForm.tsx";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import { useMemo, useState } from "react";
import dayjs from "$/utils/dayjs";
import Limits from "$/_const/Limits.ts";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import {
  TripLayoutProvider,
  useTripLayoutContext,
} from "@/layouts/TripLayout/TripLayoutProvider.tsx";
import DateRangePickerInput from "@/components/functional/DateRangePickerInput/DateRangePickerInput.tsx";
import shouldCollectBookings from "@/utils/shouldCollectBookings.ts";

const persistedDateSchema = tripCreateSchema.pick({
  arrival_date: true,
  departure_date: true,
  can_modify_arrival_or_departure_dates: true,
  duration: true,
});

const dateSchema = persistedDateSchema
  .extend({
    enterDuration: z.boolean().optional(),
  })
  .superRefine(
    ({ arrival_date, departure_date, enterDuration, duration }, ctx) => {
      try {
        if (enterDuration) {
          if (!duration || duration <= 0) {
            ctx.addIssue({
              code: "custom",
              message: "Duration is required.",
              path: ["duration"],
            });
          }

          return;
        }

        if (!arrival_date) {
          ctx.addIssue({
            code: "custom",
            message: "Arrival date is required.",
            path: ["arrival_date"],
          });
        }
        if (!departure_date) {
          ctx.addIssue({
            code: "custom",
            message: "Departure date is required.",
            path: ["departure_date"],
          });
        }

        if (!arrival_date || !departure_date) return;

        const now = dayjs();
        const departure = dayjs(departure_date);
        const arrival = dayjs(arrival_date);

        if (now.isAfter(arrival)) {
          ctx.addIssue({
            code: "custom",
            message: "Arrival date must be after today.",
            path: ["arrival_date"],
          });
        }

        if (now.isAfter(departure)) {
          ctx.addIssue({
            code: "custom",
            message: "Departure date must be after today.",
            path: ["departure_date"],
          });
        }

        if (arrival.isAfter(departure)) {
          ctx.addIssue({
            code: "custom",
            message: "Arrival date must be before departure date",
            path: ["arrival_date"],
          });
        }

        if (Math.abs(arrival.diff(departure, "day")) > Limits.TRIP_DURATION) {
          ctx.addIssue({
            code: "custom",
            message:
              `Trip duration cannot be longer than ${Limits.TRIP_DURATION} days`,
            path: ["departure_date"],
          });
        }
      } catch (e) {
        ctx.addIssue({
          code: "custom",
          message: "Invalid Arrival or Departure date",
          path: ["departure_date"],
        });
        ctx.addIssue({
          code: "custom",
          message: "Invalid Arrival or Departure date",
          path: ["arrival_date"],
        });
      }
    },
  );

export type DateSchema = z.infer<typeof dateSchema>;

type DateStepProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

function DateStepNavigation() {
  const { isMenuOpen, setIsMenuOpen } = useTripLayoutContext();

  return (
    <div
      className={`fixed inset-0 z-50 ${
        isMenuOpen
          ? "visible pointer-events-auto"
          : "invisible pointer-events-none"
      }`}
      aria-hidden={!isMenuOpen}
    >
      <button
        type="button"
        aria-label="Close navigation menu"
        className={`absolute inset-0 bg-black/45 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      <TripLayout.NavigationPanel
        className={`absolute inset-y-0 left-0 transition-transform duration-300 ease-out ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      />
    </div>
  );
}

function DateStep({ onComplete, onCompleteWhenDirty }: DateStepProps) {
  const { t } = useTranslation();
  const {
    nextStep,
    setContextData,
    contextData,
    previousStep,
    currentStepIndex,
    steps,
  } = useStepperContext<
    typeof allTripCreateSteps,
    TripData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const minSelectableDate = useMemo(
    () => dayjs().add(1, "day").startOf("day").toDate(),
    [],
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    watch,
    clearErrors,
    setValue,
  } = useForm<DateSchema>({
    resolver: zodResolver(dateSchema),
    defaultValues: {
      arrival_date: contextData?.arrival_date,
      departure_date: contextData?.departure_date,
      duration: contextData?.duration,
      enterDuration: contextData?.duration !== undefined,
      can_modify_arrival_or_departure_dates:
        contextData?.can_modify_arrival_or_departure_dates ??
          DateFlexibility.FLEXIBLE_BY_A_WEEK,
    },
  });

  const enterDuration = watch("enterDuration") ?? false;
  const arrivalDate = watch("arrival_date");
  const departureDate = watch("departure_date");
  const translatedHeading = t("tripCreateForm.date.header").replace(
    /^Great choice!\s*/,
    "",
  );
  const [headingHighlight, ...headingRest] = translatedHeading.split(" ");

  const onSubmit = (formData: DateSchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const data = persistedDateSchema.parse(formData);
        const collectsBookings = shouldCollectBookings(data);
        const context = {
          ...contextData,
          ...data,
          ...(collectsBookings ? {} : { what_has_been_booked: [] }),
        };
        const hasStaleBookings = !collectsBookings &&
          (contextData?.what_has_been_booked?.length ?? 0) > 0;
        const args: OnCompleteArgs = {
          step: TripCreateStep.DATE,
          data,
          context,
        };
        if (isDirty || hasStaleBookings) {
          setContextData(() => context);
          await onCompleteWhenDirty?.(args);
        }
        await onComplete?.(args);
        nextStep();
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
        console.error(e);
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <TripLayoutProvider>
      <DateStepNavigation />

      <div
        className="min-h-[100dvh] max-w-none overflow-x-hidden bg-journi-theme-light text-[#191919]"
        style={{
          position: "relative",
          left: "50%",
          width: "100vw",
          minWidth: "100vw",
          maxWidth: "none",
          transform: "translateX(-50%)",
        }}
      >
        <header className="flex w-full items-center justify-between gap-6 border-b border-primary/60 px-5 py-5 sm:px-8 md:px-14 lg:px-20">
          <div className="flex items-center gap-4">
            <TripLayout.OpenMenuButton color="primary" />

            <span className="font-feature text-3xl font-bold">
              Journi
            </span>
          </div>

          <StepIndicator
            numberOfSteps={steps.length}
            currentStepIndex={currentStepIndex}
          />
        </header>

        <main className="w-full px-5 py-10 sm:px-8 md:px-[5vw] md:py-14">
          <form
            className="mx-auto flex w-full max-w-[1700px] flex-col"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            {error
              ? (
                <Alert status="error" className="mb-7">
                  {error.message}
                </Alert>
              )
              : null}

            <h1 className="font-feature text-4xl font-bold leading-tight md:text-5xl">
              <span className="italic text-primary">
                {headingHighlight}
              </span>{" "}
              {headingRest.join(" ")}
            </h1>

            <div className="mt-7">
              {enterDuration
                ? (
                  <Controller
                    control={control}
                    name="duration"
                    render={({ field, fieldState }) => (
                      <div className="flex w-full max-w-[860px] flex-col items-start text-left">
                        <label
                          htmlFor="trip-duration"
                          className="sr-only"
                        >
                          {t("placeholder.duration")}
                        </label>

                        <input
                          id="trip-duration"
                          type="number"
                          min={1}
                          max={Limits.TRIP_DURATION}
                          value={field.value ?? ""}
                          disabled={isPending}
                          placeholder={t("placeholder.duration")}
                          className="h-[60px] w-full rounded-[14px] border border-primary bg-white px-6 text-left font-sans text-base text-[#191919] outline-none focus:ring-2 focus:ring-primary/20"
                          onChange={(event) =>
                            field.onChange(
                              event.target.value
                                ? Number(event.target.value)
                                : undefined,
                            )}
                        />

                        {fieldState.error
                          ? (
                            <p
                              className="mt-2 text-sm font-semibold text-error"
                              role="alert"
                            >
                              {fieldState.error.message}
                            </p>
                          )
                          : null}
                      </div>
                    )}
                  />
                )
                : (
                  <DateRangePickerInput
                    value={{
                      from: arrivalDate,
                      to: departureDate,
                    }}
                    minDate={minSelectableDate}
                    maxRangeDays={Limits.TRIP_DURATION}
                    size="lg"
                    color="primary"
                    bordered
                    placeholder={t(
                      "tripCreateForm.date.selectDates",
                      "Select your dates",
                    )}
                    error={errors.arrival_date?.message ??
                      errors.departure_date?.message}
                    wrapperClassName={`w-full max-w-[860px] [&>button]:h-[60px] [&>button]:rounded-[14px] [&>button]:border [&>button]:border-primary [&>button]:bg-white [&>button]:px-6 [&>button]:text-base [&>button]:text-[#191919] ${
                      isPending ? "pointer-events-none opacity-60" : ""
                    }`}
                    onChange={({ from, to }) => {
                      setValue("arrival_date", from, {
                        shouldDirty: true,
                        shouldValidate: false,
                      });
                      setValue("departure_date", to, {
                        shouldDirty: true,
                        shouldValidate: false,
                      });
                      clearErrors(["arrival_date", "departure_date"]);
                    }}
                  />
                )}

              <div className="mt-10 grid items-center gap-5 md:grid-cols-2">
                <p className="font-feature text-base font-semibold italic text-text-secondary md:text-lg">
                  {t("tripCreateForm.date.info")}
                </p>

                <Controller
                  control={control}
                  name="enterDuration"
                  render={({ field }) => (
                    <label className="flex cursor-pointer items-center gap-4 font-sans text-lg font-semibold text-[#8A6D23] md:justify-self-end md:text-xl">
                      <span>
                        {t("tripCreateForm.date.checkboxLabel")}
                      </span>

                      <input
                        type="checkbox"
                        checked={!!field.value}
                        disabled={isPending}
                        className="h-7 w-7 cursor-pointer rounded border-2 border-primary accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                        onChange={(event) => {
                          const checked = event.target.checked;
                          field.onChange(checked);
                          clearErrors([
                            "duration",
                            "arrival_date",
                            "departure_date",
                          ]);

                          if (checked) {
                            setValue("arrival_date", undefined, {
                              shouldDirty: true,
                            });
                            setValue("departure_date", undefined, {
                              shouldDirty: true,
                            });
                          } else {
                            setValue("duration", undefined, {
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                    </label>
                  )}
                />
              </div>
            </div>

            {!enterDuration
              ? (
                <section className="mt-12">
                  <h2 className="font-sans text-xl font-semibold text-[#8A6D23] md:text-2xl">
                    {t("tripCreateForm.date.subheader")}
                  </h2>

                  <Controller
                    control={control}
                    name="can_modify_arrival_or_departure_dates"
                    render={({ field, fieldState }) => (
                      <div className="mx-auto mt-7 flex w-full max-w-[460px] flex-col gap-5 md:w-[68vw] md:max-w-[1320px]">
                        {allDateFlexibility.map((option) => {
                          const isSelected = field.value === option;

                          return (
                            <button
                              key={option}
                              type="button"
                              disabled={isPending}
                              aria-pressed={isSelected}
                              className={`flex h-[60px] w-full items-center justify-between rounded-[18px] border border-primary px-5 text-left font-sans text-base font-normal transition-colors md:px-7 ${
                                isSelected
                                  ? "bg-primary/15 text-[#191919]"
                                  : "bg-white text-[#191919] hover:bg-primary/5"
                              }`}
                              onClick={() => field.onChange(option)}
                            >
                              <span>
                                {t(
                                  `enum_translations.DateFlexibility.${option}`,
                                )}
                              </span>

                              <span
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[6px] border-primary bg-white"
                                aria-hidden="true"
                              >
                                {isSelected
                                  ? (
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                                  )
                                  : null}
                              </span>
                            </button>
                          );
                        })}

                        {fieldState.error
                          ? (
                            <p
                              className="text-sm font-semibold text-error"
                              role="alert"
                            >
                              {fieldState.error.message}
                            </p>
                          )
                          : null}
                      </div>
                    )}
                  />
                </section>
              )
              : null}

            <div className="mt-10 flex flex-col items-center gap-3 pb-8">
              <LoadingButton
                loading={isPending}
                type="submit"
                color="primary"
                endIcon={<ChevronRightIcon className="h-3.5 w-3.5" />}
                className="journi-primary-button w-full sm:w-[250px]"
              >
                {t("button.continue")}
              </LoadingButton>

              <button
                type="button"
                className="min-h-11 px-6 font-sans text-base font-semibold text-[#191919] hover:text-primary"
                onClick={previousStep}
              >
                {t("button.goBack")}
              </button>
            </div>
          </form>
        </main>
      </div>
    </TripLayoutProvider>
  );
}

export default DateStep;
